/**
 * PixelInspector turns map picks / hover into a `RasterPixelSample`, writes it
 * to `useViewerStore.rasterToolCard.lastSample`, and anchors an ArcGIS-style
 * value popup at non-vector clicks.
 *
 * Live Spectral hover attaches directly to the map engine (MapLibre /
 * OpenLayers) and also accepts shell-provided Giro3D cursor coords. Click
 * sampling via `selection.point` still works as a lock-in.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePluginStore } from '@/state/usePluginStore';
import {
  useViewerStore,
  type RasterPixelSample,
} from '@/state/useViewerStore';
import { useViewerSlice } from '@/state/useViewerSlice';
import { getMapLibreHostHandle } from '@/engines/maplibre/hostHandle';
import { getCanvasMapHostHandle } from '@/engines/openlayers/hostHandle';
import { Icon, tokens } from '@/design';

const TILES_BASE = (
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TILES_URL) ||
  'http://localhost:8000'
).replace(/\/+$/, '');

/** Hover sample cadence — keeps the map smooth under continuous pointer move. */
const HOVER_THROTTLE_MS = 80;

export interface PixelInspectorProps {
  container: HTMLElement | null;
  cogUrl: string | null;
  /** Human-readable layer name shown in the click popup. */
  layerLabel?: string;
  /** DTM/DSM/terrain layers use an elevation-specific value label. */
  elevationLike?: boolean;
  /**
   * Optional live cursor lon/lat from the shell (Giro3D status bar).
   * MapLibre / OpenLayers attach their own listeners; this supplements them.
   */
  hoverLonLat?: readonly [number, number] | null;
  /** Optional fetch hook for tests; defaults to `globalThis.fetch`. */
  fetcher?: typeof fetch;
}

export interface RasterPixelPopupState {
  readonly x: number;
  readonly y: number;
  readonly placement?: 'above' | 'below';
  readonly align?: 'left' | 'right';
  readonly lon: number;
  readonly lat: number;
  readonly sample: RasterPixelSample | null;
}

function formatPopupValue(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(3);
}

export function RasterPixelPopup({
  state,
  layerLabel,
  elevationLike = false,
  onClose,
}: {
  readonly state: RasterPixelPopupState;
  readonly layerLabel?: string;
  readonly elevationLike?: boolean;
  readonly onClose: () => void;
}) {
  const { sample } = state;
  const firstValue = sample?.values.find((value) => Number.isFinite(value));
  const valueHeading = elevationLike ? 'Elevation' : 'Pixel value';

  return (
    <section
      data-testid="raster-pixel-popup"
      data-raster-pixel-popup
      role="dialog"
      aria-label={`${valueHeading} at clicked point`}
      style={{
        position: 'absolute',
        left: state.x,
        top: state.y,
        zIndex: tokens.zIndex.tooltip,
        width: tokens.width.popoverXs,
        maxWidth: `calc(100% - ${tokens.space[6]}px)`,
        transform: `translate(${
          state.align === 'right'
            ? `calc(-100% - ${tokens.space[3]}px)`
            : `${tokens.space[3]}px`
        }, ${
          state.placement === 'below'
            ? `${tokens.space[3]}px`
            : `calc(-100% - ${tokens.space[3]}px)`
        })`,
        padding: tokens.space[3],
        border: tokens.border.thin,
        borderRadius: tokens.radius.md,
        background: tokens.color.overlay.tooltip,
        boxShadow: tokens.elevation[2],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: tokens.space[2],
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: tokens.font.size.sm,
              fontWeight: tokens.font.weight.semibold,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={layerLabel}
          >
            {layerLabel ?? 'Raster'}
          </div>
          <div
            style={{
              marginTop: tokens.space[1],
              color: tokens.color.ink.secondary,
              fontSize: tokens.font.size.xs,
            }}
          >
            {valueHeading}
          </div>
        </div>
        <button
          type="button"
          aria-label="Close pixel value popup"
          onClick={onClose}
          data-raster-pixel-popup
          style={{
            display: 'grid',
            placeItems: 'center',
            width: tokens.density.rowDense,
            height: tokens.density.rowDense,
            padding: 0,
            border: 0,
            borderRadius: tokens.radius.sm,
            background: 'transparent',
            color: tokens.color.ink.secondary,
            cursor: 'pointer',
          }}
        >
          <Icon name="close" size={20} decorative />
        </button>
      </div>

      <div
        style={{
          marginTop: tokens.space[2],
          fontFamily: tokens.font.family.mono,
          fontSize: tokens.font.size.lg,
          fontWeight: tokens.font.weight.semibold,
        }}
      >
        {!sample
          ? 'Loading…'
          : sample.state === 'error'
            ? 'Unavailable'
            : sample.state === 'nodata'
              ? 'No data'
              : firstValue == null
                ? '—'
                : formatPopupValue(firstValue)}
      </div>

      {sample?.state === 'ok' && sample.values.length > 1 ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            gap: `${tokens.space[1]}px ${tokens.space[3]}px`,
            marginTop: tokens.space[2],
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.size.xs,
          }}
        >
          {sample.values.map((value, index) => (
            <div key={`${sample.bandNames[index] ?? 'band'}-${index}`} style={{ display: 'contents' }}>
              <span style={{ color: tokens.color.ink.secondary }}>
                {sample.bandNames[index] ?? `b${index + 1}`}
              </span>
              <span>{formatPopupValue(value)}</span>
            </div>
          ))}
        </div>
      ) : null}

      {sample?.state === 'error' ? (
        <div
          style={{
            marginTop: tokens.space[2],
            color: tokens.color.semantic.warning,
            fontSize: tokens.font.size.xs,
          }}
        >
          {sample.errorMessage ?? 'Pixel sampling failed'}
        </div>
      ) : null}

      <div
        style={{
          marginTop: tokens.space[2],
          color: tokens.color.ink.muted,
          fontFamily: tokens.font.family.mono,
          fontSize: tokens.font.size.xs,
        }}
      >
        {state.lon.toFixed(6)}, {state.lat.toFixed(6)}
      </div>
    </section>
  );
}

/**
 * Pure mapper used by the inspector AND by `__tests__` to assert
 * branching without going through `fetch`. Exported so tests can call it
 * directly with synthetic TiTiler responses.
 */
export function buildPixelSample(input: {
  lon: number;
  lat: number;
  payload: { values?: number[]; band_names?: string[] } | null;
  httpOk: boolean;
  errorMessage?: string;
}): RasterPixelSample {
  const { lon, lat, payload, httpOk, errorMessage } = input;
  if (!httpOk || !payload) {
    return {
      lon,
      lat,
      values: [],
      bandNames: [],
      state: 'error',
      errorMessage: errorMessage ?? 'request failed',
    };
  }
  const values = payload.values ?? [];
  const bandNames =
    payload.band_names ?? values.map((_, i) => `b${i + 1}`);
  if (values.length === 0 || values.every((v) => !Number.isFinite(v))) {
    return { lon, lat, values, bandNames, state: 'nodata' };
  }
  return { lon, lat, values, bandNames, state: 'ok' };
}

function samplesEquivalent(a: RasterPixelSample | null, b: RasterPixelSample): boolean {
  if (!a) return false;
  if (a.state !== b.state) return false;
  if (a.lon !== b.lon || a.lat !== b.lat) return false;
  if (a.values.length !== b.values.length) return false;
  for (let i = 0; i < a.values.length; i += 1) {
    if (a.values[i] !== b.values[i]) return false;
  }
  return a.errorMessage === b.errorMessage;
}

export function PixelInspector({
  container,
  cogUrl,
  layerLabel,
  elevationLike = false,
  hoverLonLat,
  fetcher,
}: PixelInspectorProps) {
  const activePlugin = usePluginStore((s) => s.activePluginId);
  const selection = useViewerSlice((s) => s.selection);
  const liveSpectralEnabled = useViewerSlice(
    (s) => s.rasterToolCard.liveSpectralEnabled,
  );
  const setRasterToolCard = useViewerStore((s) => s.setRasterToolCard);

  const enabled = activePlugin === null && !!cogUrl;
  // Hover when the Location & Spectral section is open (or flag unset mid-HMR).
  const hoverEnabled = enabled && liveSpectralEnabled;
  const [popup, setPopup] = useState<RasterPixelPopupState | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const clickAbortRef = useRef<AbortController | null>(null);
  const lastHoverAtRef = useRef(0);
  const pendingHoverRef = useRef<{ lon: number; lat: number } | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cogUrlRef = useRef(cogUrl);
  const fetcherRef = useRef(fetcher);
  const hoverEnabledRef = useRef(hoverEnabled);
  const latestLonLatRef = useRef<readonly [number, number] | null>(hoverLonLat ?? null);
  const lastClickPositionRef = useRef<{
    x: number;
    y: number;
    placement: 'above' | 'below';
    align: 'left' | 'right';
  } | null>(null);
  const lastClickSampleKeyRef = useRef<string | null>(null);

  cogUrlRef.current = cogUrl;
  fetcherRef.current = fetcher;
  hoverEnabledRef.current = hoverEnabled;
  if (hoverLonLat) latestLonLatRef.current = hoverLonLat;

  const fetchPoint = async (
    lon: number,
    lat: number,
    signal?: AbortSignal,
    revealPopup = false,
  ): Promise<void> => {
    const urlCog = cogUrlRef.current;
    if (!urlCog) return;
    const doFetch = fetcherRef.current ?? globalThis.fetch.bind(globalThis);
    try {
      const url = `${TILES_BASE}/cog/point/${lon},${lat}?url=${encodeURIComponent(urlCog)}`;
      const res = await doFetch(url, signal ? { signal } : undefined);
      if (signal?.aborted) return;
      if (!res.ok) {
        const next = buildPixelSample({
          lon,
          lat,
          payload: null,
          httpOk: false,
          errorMessage: `HTTP ${res.status}`,
        });
        const prev = useViewerStore.getState().rasterToolCard.lastSample;
        if (!samplesEquivalent(prev, next)) {
          setRasterToolCard({ lastSample: next });
        }
        if (revealPopup) {
          setPopup((current) =>
            current?.lon === lon && current.lat === lat
              ? { ...current, sample: next }
              : current,
          );
        }
        return;
      }
      const data = (await res.json()) as {
        coordinates?: [number, number];
        values?: number[];
        band_names?: string[];
      };
      if (signal?.aborted) return;
      const next = buildPixelSample({
        lon,
        lat,
        payload: data,
        httpOk: true,
      });
      const prev = useViewerStore.getState().rasterToolCard.lastSample;
      if (!samplesEquivalent(prev, next)) {
        setRasterToolCard({ lastSample: next });
      }
      if (revealPopup) {
        setPopup((current) =>
          current?.lon === lon && current.lat === lat
            ? { ...current, sample: next }
            : current,
        );
      }
    } catch (err) {
      if (signal?.aborted) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const next = buildPixelSample({
        lon,
        lat,
        payload: null,
        httpOk: false,
        errorMessage:
          err instanceof Error ? err.message : 'network error',
      });
      const prev = useViewerStore.getState().rasterToolCard.lastSample;
      if (!samplesEquivalent(prev, next)) {
        setRasterToolCard({ lastSample: next });
      }
      if (revealPopup) {
        setPopup((current) =>
          current?.lon === lon && current.lat === lat
            ? { ...current, sample: next }
            : current,
        );
      }
    }
  };

  const inspectClick = (lon: number, lat: number) => {
    if (!enabled || !container || !Number.isFinite(lon) || !Number.isFinite(lat)) return;
    const key = `${lon},${lat}`;
    if (lastClickSampleKeyRef.current === key) return;
    lastClickSampleKeyRef.current = key;
    const position = lastClickPositionRef.current;
    if (!position) return;
    setPopup({ ...position, lon, lat, sample: null });
    clickAbortRef.current?.abort();
    const controller = new AbortController();
    clickAbortRef.current = controller;
    void fetchPoint(lon, lat, controller.signal, true);
  };
  const inspectClickRef = useRef(inspectClick);
  inspectClickRef.current = inspectClick;

  const scheduleHoverSample = (lon: number, lat: number) => {
    if (!hoverEnabledRef.current || !cogUrlRef.current) return;
    pendingHoverRef.current = { lon, lat };
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const elapsed = now - lastHoverAtRef.current;
    const run = () => {
      if (!hoverEnabledRef.current) return;
      const pending = pendingHoverRef.current;
      if (!pending) return;
      pendingHoverRef.current = null;
      lastHoverAtRef.current =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      void fetchPoint(pending.lon, pending.lat, controller.signal);
    };
    if (elapsed >= HOVER_THROTTLE_MS) {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      run();
      return;
    }
    if (hoverTimerRef.current) return;
    hoverTimerRef.current = setTimeout(() => {
      hoverTimerRef.current = null;
      run();
    }, HOVER_THROTTLE_MS - elapsed);
  };

  // Keep a stable ref so map listeners attached once always call the latest scheduler.
  const scheduleHoverSampleRef = useRef(scheduleHoverSample);
  scheduleHoverSampleRef.current = scheduleHoverSample;

  // Click-to-sample fallback for engine host selection events.
  useEffect(() => {
    if (!enabled) return;
    if (!selection?.point || selection.point.length < 2) return;
    // Vector feature clicks belong to View Info, not raster identify.
    if (selection.layerId !== '__map__') return;
    const [lon, lat] = selection.point;
    if (typeof lon !== 'number' || typeof lat !== 'number') return;
    inspectClickRef.current(lon, lat);
  }, [enabled, selection, cogUrl, fetcher, setRasterToolCard]);

  // Remember the click's screen position and resolve it against the latest
  // engine lon/lat. Deferring one task lets vector hit-testing update the host
  // selection first, so vector clicks never open this raster popup.
  useEffect(() => {
    if (!enabled || !container) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (target?.closest('[data-raster-pixel-popup]')) return;
      const rect = container.getBoundingClientRect();
      lastClickPositionRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        placement: event.clientY - rect.top < rect.height / 2 ? 'below' : 'above',
        align: event.clientX - rect.left < rect.width / 2 ? 'left' : 'right',
      };
      const point = latestLonLatRef.current;
      if (!point) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const currentSelection = useViewerStore.getState().selection;
        const pickedPoint = currentSelection?.point;
        const samePoint =
          pickedPoint != null
          && pickedPoint.length >= 2
          && Math.abs(pickedPoint[0] - point[0]) < 1e-7
          && Math.abs(pickedPoint[1] - point[1]) < 1e-7;
        if (samePoint && currentSelection?.layerId !== '__map__') return;
        inspectClickRef.current(point[0], point[1]);
      }, 0);
    };
    container.addEventListener('click', onClick, true);
    return () => {
      container.removeEventListener('click', onClick, true);
      if (timer) clearTimeout(timer);
    };
  }, [enabled, container]);

  // Direct MapLibre / OpenLayers pointer listeners — reliable live hover.
  // Retries briefly if the host handle is not mounted yet.
  useEffect(() => {
    if (!hoverEnabled || !container || !cogUrl) return;

    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const attach = (): boolean => {
      if (cancelled) return true;

      const maplibre = getMapLibreHostHandle(container);
      if (maplibre) {
        const onMove = (event: { lngLat: { lng: number; lat: number } }) => {
          latestLonLatRef.current = [event.lngLat.lng, event.lngLat.lat];
          scheduleHoverSampleRef.current(event.lngLat.lng, event.lngLat.lat);
        };
        const onLeave = () => {
          pendingHoverRef.current = null;
          latestLonLatRef.current = null;
        };
        maplibre.on('mousemove', onMove);
        maplibre.on('mouseout', onLeave);
        unsubscribe = () => {
          try {
            maplibre.off('mousemove', onMove);
            maplibre.off('mouseout', onLeave);
          } catch {
            /* map may be torn down */
          }
        };
        return true;
      }

      const canvas = getCanvasMapHostHandle(container) as
        | { subscribePointerLonLat?: (cb: (ll: readonly [number, number] | null) => void) => () => void }
        | null;
      if (canvas?.subscribePointerLonLat) {
        unsubscribe = canvas.subscribePointerLonLat((ll) => {
          if (!ll) {
            pendingHoverRef.current = null;
            latestLonLatRef.current = null;
            return;
          }
          latestLonLatRef.current = ll;
          scheduleHoverSampleRef.current(ll[0], ll[1]);
        });
        return true;
      }

      return false;
    };

    if (!attach()) {
      // Map host may mount a tick after the container — retry a few times.
      let attempts = 0;
      const retry = () => {
        if (cancelled || attempts > 20) return;
        attempts += 1;
        if (!attach()) {
          retryTimer = setTimeout(retry, 100);
        }
      };
      retryTimer = setTimeout(retry, 50);
    }

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      unsubscribe?.();
      abortRef.current?.abort();
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    };
  }, [hoverEnabled, container, cogUrl]);

  // Giro3D (and any shell cursor) — supplements engine listeners above.
  useEffect(() => {
    if (!hoverEnabled) return;
    if (!hoverLonLat || hoverLonLat.length < 2) return;
    const [lon, lat] = hoverLonLat;
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    latestLonLatRef.current = hoverLonLat;
    // Skip if MapLibre already owns the pointer (avoids double sample storms).
    if (container && getMapLibreHostHandle(container)) return;
    if (container && getCanvasMapHostHandle(container)) return;
    scheduleHoverSampleRef.current(lon, lat);
  }, [hoverEnabled, hoverLonLat, container]);

  // Cleanup when hover disabled.
  useEffect(() => {
    if (hoverEnabled) return;
    abortRef.current?.abort();
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, [hoverEnabled]);

  useEffect(() => () => {
    abortRef.current?.abort();
    clickAbortRef.current?.abort();
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
  }, []);

  // Crosshair while inspect is active.
  useEffect(() => {
    if (!container) return;
    const map = getMapLibreHostHandle(container);
    if (!map) return;
    if (hoverEnabled) {
      map.getCanvas().style.cursor = 'crosshair';
    }
    return () => {
      const canvas = map.getCanvas();
      if (canvas) canvas.style.cursor = '';
    };
  }, [container, hoverEnabled]);

  if (!container || !popup) return null;
  return createPortal(
    <RasterPixelPopup
      state={popup}
      layerLabel={layerLabel}
      elevationLike={elevationLike}
      onClose={() => {
        clickAbortRef.current?.abort();
        lastClickSampleKeyRef.current = null;
        setPopup(null);
      }}
    />,
    container,
  );
}
